import React, { useState, useRef } from "react";
import {
  Box,
  Flex,
  Menu,
  MenuItem,
  useColorModeValue,
  Stack,
  useColorMode,
  Avatar,
  Text,
  Image,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  AlertDialog, 
  AlertDialogOverlay, 
  AlertDialogContent, 
  AlertDialogHeader, 
  AlertDialogBody, 
  AlertDialogFooter, 
  AlertDialogCloseButton,
  Button,
  Divider
} from "@chakra-ui/react";
import { Link as RouterLink} from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDeleteUserMutation } from "../../slices/userApiSlice";
import { ToastService } from '../../services/toast/toastService';
import { 
    faHouse, faStar, faGift, faCircleUser, faLock, 
    faArrowRightFromBracket, faMoon, faBars, faUserMinus,
    faExclamationCircle, faGlobe, faFileContract, faMapLocationDot
} from "@fortawesome/free-solid-svg-icons"; 
import { ChangeLanguageModal } from "./ChangeLanguageModal";
import { useTranslation } from "react-i18next";
import dateUtils from "../../utils/formatDate";

const UserMenu = ({ userInfo, logOutHandler }) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const textColor = useColorModeValue('custom.900', 'teal.300');
    const menuBgColor = useColorModeValue('gray.100', 'teal.300');
    const menuTextColor = useColorModeValue('custom.800', 'custom.900');
    const menuDrawerBgColor = useColorModeValue('teal.500', 'gray.800');
    const menuHoverColor = useColorModeValue('gray.300', 'teal.500');
    const { colorMode, toggleColorMode } = useColorMode();
    const { success, error } = ToastService();
    //const navigate = useNavigate();
    const [deleteUser, { isLoading }] = useDeleteUserMutation();
    const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
    
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const cancelRef = useRef();
    const { t } = useTranslation();
    const savedLanguage = localStorage.getItem("i18nextLng") || "en";

    const [dialogConfig, setDialogConfig] = useState({
        title: "",
        message: "",
        confirmButtonText: "",
        onConfirm: () => {},
        showAlertDialogCloseButton: true,
        showCancelButton: true,
        icon: null, 
    });

    const handleChangeLanguageClick = () => {
        setIsLanguageModalOpen(true); // Open the change language modal
    };

    // Open the dialog for logout
    const handleLogoutClick = () => {
        setDialogConfig({
          title: t('dialogs.logout.title'),
          message: t('dialogs.logout.message'),
          confirmButtonText: t('dialogs.logout.confirm'),
          onConfirm: handleLogoutConfirm,
          showAlertDialogCloseButton: true,
          showCancelButton: true,
          icon: faExclamationCircle
        });
        setIsAlertOpen(true);
    };

    // Open the dialog for account deletion
    const handleDeleteAccountClick = () => {
        setDialogConfig({
          title: t('dialogs.delete_account.title'),
          message: (
            <>
              {t('dialogs.delete_account.message')}
              <br /><br />
              <strong>{t('dialogs.delete_account.consequences')}</strong>
              <br /><br />
              <ul>
                {t('dialogs.delete_account.points', { returnObjects: true }).map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
            </>
          ),
          confirmButtonText: t('dialogs.delete_account.confirm'),
          onConfirm: handleDeleteAccountConfirm,
          showAlertDialogCloseButton: true,
          showCancelButton: true,
          icon: faExclamationCircle,
        });
        setIsAlertOpen(true);
    };

    // Handle logout confirmation
    const handleLogoutConfirm = () => {
        logOutHandler();
        setIsAlertOpen(false);
        onClose();
    };

    // Handle account deletion confirmation
    const handleDeleteAccountConfirm = async () => {
        setIsAlertOpen(false);
        onClose(); 

        try {
            await deleteUser(userInfo?._id).unwrap();
            success('Account deleted successfully!');
            logOutHandler();
            setIsAlertOpen(false);
            onClose();
        } catch (err) {
            error(err.data?.message || "Failed to delete account. Please try again later.");
        }
    };

    const handleLogoutCancel = () => {
        setIsAlertOpen(false);
    };

    const NavItem = ({ to, icon, label }) => (
        <MenuItem 
            icon={<FontAwesomeIcon icon={icon} size="lg" />} 
            as={RouterLink} 
            to={to} 
            _hover={{ bg: menuBgColor, borderRadius: 'md' }} 
            fontSize="sm" 
            p={3} 
            py={2}
        >
            {label}
        </MenuItem>
    );

    const SectionTitle = ({ title }) => (
        <Text fontSize="xs" color="gray.500" fontWeight="bold" px={3} py={2}>
            {title}
        </Text>
    );

    return (
        <Box
            w={{ base: '80px', sm: '100px', md: '120px' }}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            cursor="pointer"
            onClick={onOpen}
            _hover={{ color: menuHoverColor, borderRadius: 'md' }}
            color={menuBgColor}
        >
        <FontAwesomeIcon icon={faBars} size="xl" aria-label="Menu" />
    
        <Drawer placement="left" size="xs" onClose={onClose} isOpen={isOpen}>
            <DrawerOverlay />
            <DrawerContent>
                {/* <DrawerCloseButton /> */}
                <DrawerCloseButton
                    position="absolute"
                    top={`calc(1rem + env(safe-area-inset-top))`}
                    right="1rem"
                    zIndex="1"
                    bg="whiteAlpha.300"
                    color="white"
                    _hover={{ bg: "whiteAlpha.500" }}
                    _active={{ bg: "whiteAlpha.600" }}
                    borderRadius="xl"
                    padding="0.75rem"
                    fontSize="1.25rem" // Makes the icon inside bigger
                    transition="all 0.2s ease"
                />

                {/* <DrawerHeader bgColor={menuDrawerBgColor} borderBottomWidth="1px"> */}
                {/* Drawer Header with safe area padding */}
                    <DrawerHeader 
                        bgColor={menuDrawerBgColor} 
                        borderBottomWidth="1px"
                        pt={`calc(1rem + env(safe-area-inset-top))`}
                        pb="4"
                        pl="4"
                        pr="12" // Make room for the close button
                    >
                    <Stack direction="row" spacing={4} alignItems="center">
                        <Avatar size="lg" src={userInfo?.image_url} />
                        <Stack spacing={1}>
                            <Text fontSize="lg" color="white" fontWeight="semibold">
                                {userInfo?.name}
                            </Text>
                            <Text fontSize="xs" color="gray.300">
                                {t('member_since')} {dateUtils.formatDate(userInfo?.createdAt, savedLanguage)}
                            </Text>
                            <Flex alignItems="center">
                                <Text fontSize="sm" color={textColor} mr={2}>
                                    {t('total_points')} :
                                </Text>
                                <Image src="/star.png" boxSize="20px" alt="Points Icon" mr={1} />
                                <Text fontSize="sm" color={textColor} fontWeight="bold">
                                    {dateUtils.convertToBurmeseNumerals(userInfo?.current_points ?? 0, savedLanguage)}
                                </Text>
                            </Flex>
                        </Stack>
                    </Stack>
                </DrawerHeader>
    
                <DrawerBody 
                    color={menuTextColor}
                    pb={`calc(1rem + env(safe-area-inset-bottom))`}
                >
                <Menu>
                    {/* Navigation Section */}
                    <SectionTitle title={t('menu.sections.navigation')} />
                    <Box mb={2} borderRadius={'lg'} borderWidth={'thin'}>
                        <NavItem to="/" icon={faHouse} label={t('menu.items.home')} />
                        <Divider ml={3} maxWidth={'92%'} />
                        <NavItem to="/profile" icon={faCircleUser} label={t('menu.items.profile')} />
                        <Divider ml={3} maxWidth={'92%'} />
                        <NavItem to="/shop-locations" icon={faMapLocationDot} label={t('menu.items.shop_locations')} />
                        <Divider ml={3} maxWidth={'92%'} />
                    </Box>

                    {/* Preferences Section */}
                    <SectionTitle title={t('menu.sections.preferences')} />
                    <Box mb={2} borderRadius={'lg'} borderWidth={'thin'}>
                        <MenuItem
                        icon={<FontAwesomeIcon icon={faMoon} size="lg" />}
                        _hover={{ bg: menuBgColor, borderRadius: 'md' }}
                        fontSize="sm"
                        p={3}
                        py={2}
                        onClick={toggleColorMode}
                        onMouseLeave={(e) => e.target.blur()}
                        >
                        {colorMode === 'light' ? t('menu.items.dark_mode') : t('menu.items.light_mode')}
                        </MenuItem>
                        <Divider ml={3} maxWidth={'92%'}/>
                        {userInfo?.authProvider !== 'google' && (
                            <>
                            <NavItem to="/user/change-password" icon={faLock} label={t('menu.items.change_password')} />
                            <Divider ml={3} maxWidth={'92%'}/>
                            </>
                        )}
                        <MenuItem
                            icon={<FontAwesomeIcon icon={faGlobe} size="lg" />}
                            _hover={{ bg: menuBgColor, borderRadius: 'md' }} 
                            onClick={handleChangeLanguageClick}
                            fontSize="sm"
                            p={3}
                            py={2}
                        >
                        {t('menu.items.language')}
                        </MenuItem>
                        <Divider ml={3} maxWidth={'92%'}/>
                    </Box>

                    {/* History Section */}
                    <SectionTitle title={t('menu.sections.history')} />
                    <Box mb={2} borderRadius={'lg'} borderWidth={'thin'}>
                        <NavItem to={`/point/${userInfo?._id}`} icon={faStar} label={t('menu.items.points_history')} />
                        <Divider ml={3} maxWidth={'92%'}/>
                        <NavItem to={`/reward/${userInfo?._id}`} icon={faGift} label={t('menu.items.reward_history')} />
                        <Divider ml={3} maxWidth={'92%'} />
                    </Box>

                    {/* Logout Section */}
                    <SectionTitle title={t('menu.sections.account')} />
                    <Box mb={2} borderRadius={'lg'} borderWidth={'thin'}>
                        <NavItem to={'/terms-and-conditions'} icon={faFileContract} label={t('menu.items.terms_conditions')} />
                        <Divider ml={3} maxWidth={'92%'}/>
                        <MenuItem
                            icon={<FontAwesomeIcon icon={faUserMinus} size="lg" />}
                            _hover={{ bg: menuBgColor, borderRadius: 'md' }} 
                            onClick={handleDeleteAccountClick}
                            fontSize="sm"
                            p={3}
                            py={2}
                            color="red"
                        >
                        {t('menu.items.delete_account')}
                        </MenuItem>
                    </Box>

                    <Flex justifyContent="center">
                        <Box bg="blue.500" mt={2} width="100%" borderRadius="md" color="white">
                            <MenuItem
                                onClick={handleLogoutClick}
                                _hover={{ bg: 'blue.600', borderRadius: 'md' }}
                                fontSize="sm"
                                p={3}
                                py={2}
                            >
                            <Flex alignItems="center" justifyContent="center" width="100%">
                                <FontAwesomeIcon icon={faArrowRightFromBracket} size="lg" style={{ marginRight: '8px' }} />
                                <Text>{t('menu.items.logout')}</Text>
                            </Flex>
                            </MenuItem>
                        </Box>
                    </Flex>

                    {/* App version - Add this right after the logout button */}
                    <Flex justifyContent="center" mt={4} mb={2}>
                        <Text fontSize="xs" color="gray.400">
                            Version {process.env.REACT_APP_VERSION || '1.0.0'}
                        </Text>
                    </Flex>

                    {/* Alert Dialogs */}
                    <AlertDialog
                        isOpen={isAlertOpen}
                        leastDestructiveRef={cancelRef}
                        onClose={() => setIsAlertOpen(false)}
                        closeOnOverlayClick={false}
                        >
                        <AlertDialogOverlay>
                            <AlertDialogContent>
                            <AlertDialogHeader fontSize="sm" fontWeight="bold">
                                {dialogConfig.icon && (
                                <FontAwesomeIcon 
                                    icon={dialogConfig.icon} 
                                    style={{ 
                                    marginRight: '8px', 
                                    color: '#ff9800',
                                    fontSize: '20px'
                                    }} 
                                />
                                )}
                                {dialogConfig.title}
                                {dialogConfig.showAlertDialogCloseButton && (
                                <AlertDialogCloseButton position="absolute" right="8px" top="8px" />
                                )}
                            </AlertDialogHeader>
                            <AlertDialogBody fontSize="sm">
                                {typeof dialogConfig.message === 'string' 
                                ? dialogConfig.message
                                : dialogConfig.message}
                            </AlertDialogBody>
                            <AlertDialogFooter>
                                {dialogConfig.showCancelButton && (
                                <Button 
                                    size="sm" 
                                    ref={cancelRef} 
                                    onClick={handleLogoutCancel}  // Now using the handler
                                    >
                                    {t('cancel')}
                                </Button>
                                )}
                                <Button
                                size="sm"
                                colorScheme="red"
                                onClick={dialogConfig.onConfirm}
                                ml={3}
                                isLoading={isLoading}
                                >
                                {dialogConfig.confirmButtonText}
                                </Button>
                            </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialogOverlay>
                        </AlertDialog>

                    <ChangeLanguageModal 
                        isOpen={isLanguageModalOpen} 
                        onClose={() => setIsLanguageModalOpen(false)} 
                    />
                    </Menu>
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    </Box>
    
    );
};

export default UserMenu;